import {
  Children,
  PropsWithChildren,
  ReactNode,
  useContext,
  useState,
} from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@radix-ui/react-collapsible';
import { ChevronDown } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { DataListContext } from '@components/patterns/DataList/context';
import { DataListItemVariant } from '@components/patterns/DataList/types';
import { cn, easeOutSmooth } from '@components/primitives/utilities';

import pluralize from '@utils/pluralize';

import css from './styles.module.css';

const propertyLabel = {
  singular: 'property',
  plural: 'properties',
};

export interface DataListGroupProps extends PropsWithChildren {
  label: string;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?(open: boolean): void;
  expandable?: boolean;
  value?: ReactNode;
  variant?: DataListItemVariant;
  suffix?: ReactNode;
}

export function DataListGroup({
  children,
  label,
  value,
  variant,
  suffix,
  ...props
}: DataListGroupProps) {
  const ctx = useContext(DataListContext);

  const defaultExpandable = value ? false : true;
  const expandable = props.expandable ?? defaultExpandable;

  const [openState, setOpenState] = useState(props.defaultOpen);
  const open = !expandable ? true : (props.open ?? openState);
  const onOpenChange = props.onOpenChange ?? setOpenState;

  const count = Children.toArray(children).filter(Boolean).length;

  return (
    <Collapsible
      className={cn(css.item, css.group)}
      open={open}
      onOpenChange={onOpenChange}
    >
      <dt className={css.groupLabel}>
        <span className={css.label}>
          <span className={css.labelText}>
            {label}
            {expandable && (
              <CollapsibleTrigger className={css.expandButton}>
                <ChevronDown aria-hidden />
                <span className="sr-only">{open ? 'Collapse' : 'Expand'}</span>
              </CollapsibleTrigger>
            )}
          </span>
        </span>
      </dt>

      <dd className={cn(css.value, css.groupValue)}>
        <span className={css.valueText}>
          <span className={css.valueTextContent}>
            <span data-variant={variant}>{value}</span>

            <AnimatePresence initial={false}>
              {!open && !value ? (
                <motion.span
                  key="hint"
                  className={css.expandableHint}
                  initial={{ opacity: 0, y: 3 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 3 }}
                  transition={{ duration: 0.25, ease: easeOutSmooth }}
                >
                  Show {pluralize(count, propertyLabel)}
                </motion.span>
              ) : (
                <span key="placeholder" className={css.expandablePlaceholder}>
                  {' '}
                </span>
              )}
            </AnimatePresence>
          </span>
        </span>

        {suffix && <span className={css.valueSuffix}>{suffix}</span>}
      </dd>

      <dd className={css.groupContent}>
        <AnimatePresence initial={false}>
          {open && (
            <CollapsibleContent forceMount asChild>
              <motion.div
                className={css.groupRoot}
                initial={{ opacity: 0, y: -3, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -3, height: 0 }}
                transition={{ duration: 0.25, ease: easeOutSmooth }}
              >
                <dl
                  className={css.root}
                  style={{ '--depth': ctx.groups.length + 1 }}
                >
                  <DataListContext.Provider
                    value={{ ...ctx, groups: [...ctx.groups, label] }}
                  >
                    {children}
                  </DataListContext.Provider>
                </dl>
              </motion.div>
            </CollapsibleContent>
          )}
        </AnimatePresence>
      </dd>
    </Collapsible>
  );
}
